import { CURRENT_ACCOUNT_ID, REFRESH_TOKEN, TOKEN, THEME } from 'constants/localStorageConstants';
import {
	getSelectedBasemapFromLS,
	getSelectedMapBoxStyleFromLS,
	getAirspaceMapBoundsFromLS,
	getAirspaceMapPinFromLS,
	getUnitsFromLS,
	getSelectedMapStyleFromLS,
	getSelectedMapSourcesFromLS
} from 'actions/localStorage';
import { emptyFeatureCollection } from 'utils/mapsUtils';
import { browserTimeZone } from 'utils/timeHelpers';
import dayjs from 'utils/customDayJS';
import {
	getOwnshipRing1FromLS,
	getOwnshipRing2FromLS,
	getOwnshipRing3FromLS,
	getLiveTrafficMetricSpeedUnitFromLS,
	getLiveTrafficImperialSpeedUnitFromLS,
	getLiveTrafficMaxAltitudeImperialFromLS,
	getLiveTrafficMaxAltitudeMetricFromLS,
	getLiveTrafficLowAltitudeColorFromLS,
	getLiveTrafficMidAltitudeColorFromLS,
	getLiveTrafficHighAltitudeColorFromLS,
	getLiveTrafficAltitudeThresholdsMetricFromLS,
	getLiveTrafficAltitudeThresholdsImperialFromLS
} from 'actions/localStorage';

export const initialGlobalState = {
	isLoading: false,
	showReloadMsg: {
		isOpen: false,
		versionUpdate: null,
		shouldClearStorage: false,
		newVersion: null
	},
	snackbarAlerts: [
		//see structure in AlertMessage.js propTypes
	],
	allAircraftMakes: [],
	allAircraftModels: [],
	allBatteryMakes: [],
	allBatteryModels: [],
	accountAircraftModels: [],
	components: [],
	trafficPanelOpen: false,
	accountTagGroups: [],
	accountRoles: [],
	maintenanceSchedules: [],
	units: getUnitsFromLS(),
	airBossTrafficPanelOpen: false,
	users: [],
	notices: [],
	showNoticeDialog: false,
	theme: localStorage.getItem(THEME) || 'light'
};

export const initialUserAuthState = {
	token: localStorage.getItem(TOKEN),
	refreshToken: localStorage.getItem(REFRESH_TOKEN),
	currentAccountId: localStorage.getItem(CURRENT_ACCOUNT_ID)
		? Number(localStorage.getItem(CURRENT_ACCOUNT_ID))
		: null,
	amplifyError: null,
	amplifyAlert: null,
	accountExpiringDialogOpen: false,
	requestingResetCode: false,
	resetCodeSent: false
};

export const initialMapState = {
	mapLoading: false,
	userCoordinate: null,
	markerDetails: null,
	loading: false,
	userLocationLoading: false,
	locationDetailsLoading: false,
	style: getSelectedMapStyleFromLS(),
	styleOptions: [],
	mapboxSources: [],
	aloftSources: [],
	visibleMapboxSources: getSelectedMapSourcesFromLS(),
	tileServerLayers: [],
	visibleMapLayers: [],
	sources: [],
	styleConfig: null,
	weatherLoading: false,
	weather: null,
	advisoriesLoading: false,
	advisory: null,
	geolocationError: null,
	featureCollection: emptyFeatureCollection,
	updateMapFromFile: false,
	drawInstance: null,
	selectedFeatures: [],
	radiusObject: {},
	unauthenticatedStyle: getSelectedMapBoxStyleFromLS(),
	mapReadyForDrawing: false,
	clickedFeatures: [],
	searchOptions: [],
	mapBoundingBox: null,
	airspaceMapBounds: getAirspaceMapBoundsFromLS(),
	airspaceMapPin: getAirspaceMapPinFromLS(),
	renderMap: true,
	showFeatureCard: false,
	mapPinInView: true,
	mapLayers: [],
	mapSources: [],
	mapBasemaps: [],
	mapModifiers: [],
	mapSections: [],
	mapRegions: [],
	geoJsonData: {},
	basemap: getSelectedBasemapFromLS(),
	savedMapViews: null,
	savedMapViewsLoading: false,
	clickedAdvisoryId: null
};

export const initialActivitiesState = {
	activitiesList: [],
	editModeId: false
};

export const initialFileUploadState = {
	fileLoading: false,
	preUploadFilesList: [],
	filesList: []
};

export const initialFlightPathViewerState = {
	data: [],
	dataLoading: false
};

export const initialLaancState = {
	laancCreateType: 'laanc-107',
	laancEditMode: { isEdit: false, selectedId: '' },
	laancCreateCurrentStep: 1,
	laancPayload: {},
	laancCheckResult: [],
	laancCreateTimestamp: { date: null, hours: 2, mins: 0 },
	laancCreateStartTime: null,
	laancCreatePreCheckValid: false,
	laancWithInDistance: true,
	laancCeiling: 0,
	laancAirspaceStatus: {
		outsideAirspace: null,
		FC: null, // futher cooordination
		AA: null // auto approval
	},
	laancAltitude: 0,
	laancCreatePilot: null,
	laancCreateSafetyJustification: '',
	laancShapeValid: true,
	timezone: browserTimeZone
};

export const initialUSpaceState = {
	// UI State (not sent to API)
	currentStep: 1,
	editMode: { isEdit: false, selectedId: '' },
	checkResult: [],
	frontEndValidationErrors: {},
	backEndValidationErrors: [],

	// Form State (not sent to API)
	user: null,
	aircraft: null,

	// API Payload (sent to API)
	payload: {
		// User and aircraft info
		user_id: null,
		aircraft_id: null,
		uas_operator_identifier: '',
		uas_serial_number: '',

		// Operation details
		flight_type: 'normal',
		operation_mode: '',
		operation_category: '',
		uas_aircraft_class: '',
		rid_technology: '',
		connectivity_method: '',
		uas_endurance: '',
		emergency_procedure: '',

		// Time and duration (API fields)
		start_time: dayjs().unix(),
		duration: 120 // 2 hours in minutes
	}
};

export const initialLiveTrafficState = {
	airMeshFeatures: [],
	airMeshWebsocketConnected: false,
	airMeshConnectionError: false,
	airMeshConnectionLoading: false,
	airMeshPusherInstance: null,
	pauseTrafficUpdates: false,
	airMeshAircraftTypes: [],
	airMeshProviderTypes: [],
	airMeshSignalTypes: [],
	airMeshSignalProviderTypes: [],
	aircraftTypesFilterValue: [],
	providerTypesFilterValue: [],
	signalTypesFilterValue: [],
	signalProvidersFilterValue: [],
	ownshipID: null,
	showAircraftInfoList: [],
	showBearingRangeList: [],
	airMeshChannels: [],
	hideSimulatedTraffic: false,
	hasSimulatedTraffic: false,
	ownshipRing1: getOwnshipRing1FromLS(),
	ownshipRing2: getOwnshipRing2FromLS(),
	ownshipRing3: getOwnshipRing3FromLS(),
	liveTrafficMetricSpeedUnit: getLiveTrafficMetricSpeedUnitFromLS(),
	liveTrafficImperialSpeedUnit: getLiveTrafficImperialSpeedUnitFromLS(),
	liveTrafficMaxAltitudeImperial: getLiveTrafficMaxAltitudeImperialFromLS(),
	liveTrafficMaxAltitudeMetric: getLiveTrafficMaxAltitudeMetricFromLS(),
	liveTrafficLowAltitudeColor: getLiveTrafficLowAltitudeColorFromLS(),
	liveTrafficMidAltitudeColor: getLiveTrafficMidAltitudeColorFromLS(),
	liveTrafficHighAltitudeColor: getLiveTrafficHighAltitudeColorFromLS(),
	liveTrafficAltitudeThresholdsMetric: getLiveTrafficAltitudeThresholdsMetricFromLS(),
	liveTrafficAltitudeThresholdsImperial: getLiveTrafficAltitudeThresholdsImperialFromLS()
};

export const initialOperatingPictureState = {
	features: [],
	typesFilterValue: [],
	sourcesFilterValue: [],
	pagination: {
		currentPage: 1,
		totalPages: 1,
		loadedPages: [],
		isLoading: false,
		hasMorePages: true,
		isInitialLoad: true,
		tempFeatures: [],
		totalItemsLoaded: 0
	}
};

export const initialManageTablesState = {
	tableLoading: false,
	manageLastRequestUrl: null,
	manageModal: {
		isOpen: false,
		selected: null
	},
	manageDrawer: {
		isOpen: false,
		drawerState: 'view',
		selectedID: null,
		data: {}
	},
	manageSortedTable: {
		list: [],
		activeTable: null,
		sortBy: null,
		sortDirection: true,
		initialLoad: true
	},
	managePagination: {
		current_page: 0,
		last_page: 0,
		limit: 0,
		offset: 0,
		total: 0
	},
	manageAddUser: {
		modal: {
			isOpen: false
		},
		payload: {}
	},
	selectedTableRows: [],
	taggableModelsCount: null,
	tableCSVDownloading: false,
	hoverMapItems: [],
	zoomToResultsBounds: false,
	loadingFilterValues: false,
	detailDrawerLoading: false,
	manageFilterMenuOpen: false,
	missionPlanningDrawerOpen: false,
	flightMergeLoading: null,
	loadedPages: [],
	currentView: {}, // { resourceType: string, columns: [{ field: string, width: number, sort_by: bool }] }
	savedView: null // { id: number, name: string, is_global: bool, resource_type: string, config: { filters: {}, columns: [] } }
};

export const initialReportsState = {
	reportsTotals: null,
	reportsLoading: false,
	activeReports: {
		columns: [],
		rows: []
	},
	visualData: [],
	dashboardLoading: false
};

export const initialRolesState = {
	rolesPermAssignedList: []
};

export const initialStreamingState = {
	streamMicEnabled: null,
	streamsList: [],
	streamsInitiallyLoaded: false,
	streamsLoading: false,
	mapView: true,
	equalView: true,
	selectedStreams: [],
	drawerOpen: true,
	shareTokenLoading: false,
	mapBounds: null,
	micPermissionGranted: false,
	micChecked: false,
	streamsInitiallyOpened: false
};
